import type { ApiOverviewRow, ApiOverviewTable } from './api-types';
import type { Pill, TableRow } from '@/components/DataTable';
import { count, days, deltaNum, deltaPct, direction, pct } from './format';

/**
 * Maps the API's overview rows onto the table model the components render.
 *
 * The API deliberately returns numbers, not display strings — formatting is a
 * presentation concern and the same payload also feeds exports. This is the
 * single place that turns one into the other.
 */

export type Grain = 'category' | 'city' | 'sku';

function pills(grain: Grain, row: ApiOverviewRow): Pill[] | undefined {
  const out: Pill[] = [];
  if (grain === 'sku' && row.grammage) out.push({ icon: 'size', text: row.grammage });
  if (row.share_pct != null) out.push({ icon: 'share', text: `${Math.round(row.share_pct)}%` });
  if (grain === 'category' && row.sku_count != null) {
    out.push({ icon: 'count', text: String(row.sku_count) });
  }
  return out.length > 0 ? out : undefined;
}

export function adaptRow(
  grain: Grain,
  row: ApiOverviewRow,
  selectedLabel: string | undefined,
  image?: string,
): TableRow {
  const m = row.metrics;
  // The "All" summary row carries no share of itself and no pills in the comp.
  const isAll = row.label === 'All';

  return {
    id: row.id,
    label: row.label,
    value: row.label,
    // `id` is "<grain>-<identity>"; the SKU drill-down needs that identity.
    ...(grain === 'sku' && row.id.startsWith('sku-') ? { entityId: row.id.slice(4) } : {}),
    ...(image ? { image } : {}),
    ...(grain === 'sku' ? { pillGap: 10 } : {}),
    ...(isAll ? {} : { pills: pills(grain, row) }),
    ...(row.psl.band ? { psl: row.psl.band } : {}),
    selected: !isAll && row.label === selectedLabel,
    cells: {
      wt_osa: {
        value: pct(m.wt_osa.value),
        ...opt('delta', deltaPct(m.wt_osa.delta)),
        ...opt('direction', direction(m.wt_osa.delta)),
        ...opt('series', m.wt_osa.series ?? undefined),
      },
      listing: {
        value: pct(m.listing.value),
        ...opt('delta', deltaPct(m.listing.delta)),
        ...opt('direction', direction(m.listing.delta)),
        ...opt('series', m.listing.series ?? undefined),
      },
      osa_ls: {
        value: pct(m.osa_ls.value),
        ...opt('delta', deltaPct(m.osa_ls.delta)),
        ...opt('direction', direction(m.osa_ls.delta)),
        ...opt('series', m.osa_ls.series ?? undefined),
      },
      doi: {
        value: days(m.doi.value),
        ...opt('delta', deltaNum(m.doi.delta)),
        ...opt('direction', direction(m.doi.delta)),
      },
      soh: { value: count(m.soh.value) },
    },
  };
}

export function adaptTable(
  grain: Grain,
  table: ApiOverviewTable | undefined,
  selectedLabel: string | undefined,
  imageFor?: (label: string) => string | undefined,
  labelFor?: (label: string) => string | undefined,
): TableRow[] {
  if (!table) return [];
  return table.rows.map((row) => {
    const adapted = adaptRow(grain, row, selectedLabel, imageFor?.(row.label));
    const display = row.label === 'All' ? row.label : labelFor?.(row.label);
    return display ? { ...adapted, label: display } : adapted;
  });
}

/** Omit a key entirely when undefined — the table model uses exact optionals. */
function opt<K extends string, V>(key: K, value: V | undefined): Record<K, V> | object {
  return value === undefined ? {} : ({ [key]: value });
}
